"use client";

import style from "@/components/pages/custom/modals/styles/create-new-train-modal.module.scss";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";
import useCreateCustom from "@/components/pages/custom/hooks/use-create-custom";
import { useProfileContext } from "@/providers/profile-provider/profile-provider";
import { useOutOfVoices } from "@/components/pages/custom/hooks/use-out-of-voices";
import React, { useState } from "react";
import validateField from "@/components/pages/custom/utils/field-validator";
import {
  isDatasetLengthLimited,
  isVoicesLimited,
} from "@/components/pages/custom/utils/create-validator";
import StyledInput from "@/components/common/input/styled-input";
import DatasetUpload from "@/components/pages/custom/components/dataset-upload";
import LanguageDropdown from "@/components/pages/custom/components/language-dropdown";
import QualityPresetDropdown from "@/components/pages/custom/components/quality-preset-dropdown";
import UploadedFileCard from "@/components/pages/custom/components/uploaded-file-card";
import FilledButton from "@/components/common/filled-button";
import infoIcon from "@/assets/icons/info-icon.svg";
import Image from "next/image";
import { useCreditBalance } from "@/hooks/providers/credit-balance-provider";
import { LicenseName } from "@/data/license.data";
import { useLimitTransaction } from "@/hooks/credit/use-limit-transaction";
import { getUserLicense } from "@/actions/db/get-user-license";
import { ToastContainer } from "react-toastify";

const CreateNewTrainModal = () => {
  const { showModal } = useGlobalModalContext();
  const {
    datasetFiles,
    presets,
    selectedPreset,
    setSelectedPreset,
    changeDatasetFiles,
    datasetLanguage,
    changeDatasetLanguage,
    voiceName,
    changeVoiceName,
    startTrainVoice,
    isLoading,
    eta,
  } = useCreateCustom();

  const { user, license } = useProfileContext();
  const { maxCustomVoicesCount, remainingVoicesCount } = useOutOfVoices();
  const { creditBalance } = useCreditBalance();
  const { handleUseCreditsClick } = useLimitTransaction();

  const showTrialModal = (license: LicenseName) =>
    showModal({
      type: "custom-voices-limited_modal",
      props: {
        licenseName: license,
      },
    });

  const showCreditLimitModal = () =>
    showModal({
      type: "custom_voice_limit_credit_modal",
      props: {
        handleUseCreditsClick: handleUseCreditsClick,
      },
    });

  const handleDelete = (index: number) => {
    changeDatasetFiles(datasetFiles.filter((_, i) => i !== index));
  };

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleCreate = async () => {
    const textValidation = await validateField("voiceName", voiceName);
    const datasetValidation = await validateField("voiceDataset", datasetFiles);

    if (!datasetValidation.isValid || !textValidation.isValid) {
      setErrors({
        voiceName: textValidation.message,
        voiceDataset: datasetValidation.message,
      });
      return;
    }

    if (
      await isVoicesLimited(
        user,
        creditBalance,
        showTrialModal,
        showCreditLimitModal,
      )
    ) {
      return;
    }

    const isUserLicenseLimited = await isDatasetLengthLimited(
      datasetFiles,
      /// temp added to fix user problem
      /// здається проблема в тому що ліцензія не встигає підгружатись із контекста
      /// звідти і помилка про studio i 0 minutes
      (await getUserLicense(user.id)) as LicenseName,
      user.id,
    );

    if (isUserLicenseLimited) {
      showModal({
        type: "custom_dataset_limit_modal",
        props: {
          licenseName: license,
        },
      });
      return;
    }

    const startTrainResult = await startTrainVoice();
    if (startTrainResult.type === "success") {
      showModal({
        type: "success_created_custom_modal",
      });
      return;
    }

    showModal({
      type: "error_modal",
      props: {
        title: "Failed while create custom voice",
        message: startTrainResult.message,
      },
    });
  };

  const handleChangeVoiceName = async (value: string) => {
    changeVoiceName(value);
    setErrors((prevErrors) => ({ ...prevErrors, voiceName: "" }));
  };

  const handleChangeDataset = async (files: File[]) => {
    changeDatasetFiles(files);
    setErrors((prevErrors) => ({ ...prevErrors, voiceDataset: "" }));
  };

  const tutorialContainer = (
    <div className={style.tutorial_container}>
      <div
        className={style.getting_started_wrapper}
        onClick={() => showModal({ type: "how_to_train" })}
      >
        <div>Getting started is easy</div>
        <Image src={infoIcon} alt={"info"} height={24} width={24} />
      </div>
    </div>
  );

  const form = (
    <div className={style.form_wrapper}>
      <div className={style.nameField}>
        <StyledInput
          title="Voice Name"
          onChange={(event) => handleChangeVoiceName(event.target.value)}
          value={voiceName}
        />
      </div>
      {errors.voiceName && (
        <div className={style.error}>{errors.voiceName}</div>
      )}
      <div className={style.available}>
        <span>
          {remainingVoicesCount} out of {maxCustomVoicesCount}
        </span>{" "}
        voices available for training
      </div>
      <DatasetUpload
        changeDatasetFiles={handleChangeDataset}
        datasetFiles={datasetFiles}
      />
      {errors.voiceDataset && (
        <div className={style.error}>{errors.voiceDataset}</div>
      )}
      <div className={style.dropdownsRow}>
        <div className={style.dropdownWrapper}>
          <LanguageDropdown
            datasetLanguage={datasetLanguage}
            changeDatasetLanguage={changeDatasetLanguage}
          />
        </div>
        <div className={style.dropdownWrapper}>
          <QualityPresetDropdown
            presets={presets}
            selectedPreset={selectedPreset}
            handlePresetChanged={setSelectedPreset}
          />
        </div>
      </div>
      {eta && <div className={style.etaWrapper}>ETA ~ {eta}min</div>}
      <div className={style.uploadedFiles}>
        {datasetFiles.map((file, index) => (
          <UploadedFileCard
            key={index}
            title={file.name}
            deleteClick={() => handleDelete(index)}
          />
        ))}
      </div>
      <div className={style.startButton}>
        <FilledButton
          text={isLoading ? "Processing..." : "Create AI Voice"}
          disabled={isLoading}
          onClick={handleCreate}
        />
      </div>
    </div>
  );

  return (
    <>
      <ToastContainer position={"bottom-center"} />
      <div className={style.wrapper}>
        {tutorialContainer}
        {form}
      </div>
    </>
  );
};

export default CreateNewTrainModal;
